/**
 * useVisitTracker — v3.2.48
 *
 * On every route change:
 *   1. Ends the previous page's tracking (fires a beacon with
 *      dwell_ms + click_count against the previous visit_id).
 *   2. Generates a fresh visit_id, captures UTM params from the URL,
 *      and posts /track/visit.
 *   3. Starts a lightweight engagement collector: `mousedown` counts,
 *      wall-clock timer for dwell.
 *
 * Uses navigator.sendBeacon() on unload for the final flush since
 * axios/fetch calls get cancelled on page tear-down.
 *
 * v3.2.48 · Tracking is SKIPPED on preview / localhost hosts so the
 * admin dashboard only ever shows real production traffic. Match on
 * hostname: any *.preview.emergentagent.com, *.emergent.host, or
 * localhost / 127.0.0.1 is treated as a dev environment.
 */
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { api, getBackendUrl } from "@/lib/api";

const SESSION_KEY = "eengdes_visit_session";
// UTM values persist within a session so a user who arrives with
// ?utm_source=google keeps that attribution across subsequent nav.
const UTM_KEY     = "eengdes_visit_utms";

function isDevHost() {
  try {
    const h = window.location.hostname || "";
    return (
      h === "localhost" ||
      h === "127.0.0.1" ||
      h.endsWith(".preview.emergentagent.com") ||
      h.endsWith(".emergent.host")
    );
  } catch {
    return false;
  }
}

function getSessionId() {
  try {
    let s = sessionStorage.getItem(SESSION_KEY);
    if (!s) {
      s = Math.random().toString(16).slice(2) + Date.now().toString(16);
      sessionStorage.setItem(SESSION_KEY, s);
    }
    return s;
  } catch {
    return "no-storage-" + Math.random().toString(16).slice(2);
  }
}

function genVisitId() {
  return "v_" + Math.random().toString(16).slice(2) + Date.now().toString(16);
}

function readUtms(searchStr) {
  const params = new URLSearchParams(searchStr || "");
  const utms = {
    utm_source:   params.get("utm_source")   || null,
    utm_medium:   params.get("utm_medium")   || null,
    utm_campaign: params.get("utm_campaign") || null,
  };
  const hasAny = utms.utm_source || utms.utm_medium || utms.utm_campaign;
  if (hasAny) {
    try {
      sessionStorage.setItem(UTM_KEY, JSON.stringify(utms));
    } catch { /* ignore */ }
    return utms;
  }
  // Fallback — persisted UTMs from earlier in the session.
  try {
    const s = sessionStorage.getItem(UTM_KEY);
    if (s) return JSON.parse(s);
  } catch { /* ignore */ }
  return { utm_source: null, utm_medium: null, utm_campaign: null };
}

function sendEndBeacon(visit_id, dwell_ms, click_count) {
  if (!visit_id) return;
  const url = `${getBackendUrl()}/api/track/visit-end`;
  const payload = JSON.stringify({ visit_id, dwell_ms, click_count });
  try {
    if (navigator.sendBeacon) {
      const blob = new Blob([payload], { type: "application/json" });
      const ok = navigator.sendBeacon(url, blob);
      if (ok) return;
    }
  } catch { /* fall through */ }
  // Fallback for browsers without sendBeacon — best effort.
  try {
    fetch(url, {
      method: "POST",
      body: payload,
      headers: { "Content-Type": "application/json" },
      keepalive: true,
      credentials: "include",
    }).catch(() => {});
  } catch { /* ignore */ }
}

export default function useVisitTracker() {
  const { pathname, search } = useLocation();
  const stateRef = useRef({
    visit_id: null,
    started_at: 0,
    clicks: 0,
  });

  useEffect(() => {
    // v3.2.48 · Never track dev / preview environments — the admin
    // dashboard only surfaces real production traffic.
    if (isDevHost()) return;
    // Skip machine-driven transient routes.
    if (pathname.startsWith("/verify") || pathname.startsWith("/payment/")) {
      return;
    }

    // Flush metrics for the PREVIOUS page (if any).
    const prev = stateRef.current;
    if (prev.visit_id) {
      const dwell = Math.max(0, Date.now() - prev.started_at);
      sendEndBeacon(prev.visit_id, dwell, prev.clicks);
    }

    // Start tracking THIS page.
    const visit_id  = genVisitId();
    const session_id = getSessionId();
    const utms      = readUtms(search);
    stateRef.current = { visit_id, started_at: Date.now(), clicks: 0 };

    api.post("/track/visit", {
      path: pathname.slice(0, 200),
      session_id,
      visit_id,
      referrer: document.referrer || null,
      utm_source:   utms.utm_source,
      utm_medium:   utms.utm_medium,
      utm_campaign: utms.utm_campaign,
    }).catch(() => { /* ignore */ });

    // Click collector — counts real user clicks anywhere on the page.
    const onClick = () => { stateRef.current.clicks += 1; };
    window.addEventListener("mousedown", onClick, { passive: true });

    // Flush on unload / tab hide.
    const flushCurrent = () => {
      const s = stateRef.current;
      if (!s.visit_id) return;
      const dwell = Math.max(0, Date.now() - s.started_at);
      sendEndBeacon(s.visit_id, dwell, s.clicks);
    };
    const onHide = () => { if (document.visibilityState === "hidden") flushCurrent(); };
    window.addEventListener("pagehide",       flushCurrent);
    window.addEventListener("beforeunload",   flushCurrent);
    document.addEventListener("visibilitychange", onHide);

    return () => {
      window.removeEventListener("mousedown",       onClick);
      window.removeEventListener("pagehide",        flushCurrent);
      window.removeEventListener("beforeunload",    flushCurrent);
      document.removeEventListener("visibilitychange", onHide);
    };
  }, [pathname, search]);
}
